import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { useRetryWorkflow } from '~/features/catalog-operations/hooks/use-workflow-retry';
import type {
  WorkflowFailurePointsResponse,
  WorkflowRetryMode,
} from '~/features/catalog-operations/types/catalog-operations.types';
import { getRetryBlocker } from '~/features/catalog-operations/utils/workflow-retry';
import { formatDateTime } from '~/utils/date';

interface Props {
  open: boolean;
  /** Job uuid (route `$batchId`) — used only to invalidate the right batch-detail query. */
  jobId: string;
  /** Human-readable `BatchDetail.batchId` — the identifier the retry endpoint expects. */
  workflowId: string;
  /** Discovery response resolved by the page-level query. Undefined while
   *  discovery is still in flight — the confirm button stays disabled until
   *  it resolves. Carries the `runId` the retry call must target alongside
   *  the failure point's `resetToEventId`. */
  failurePoints: WorkflowFailurePointsResponse | undefined;
  onClose: () => void;
}

/** Confirm dialog for retrying a failed batch workflow from its last failure
 *  point. When that point's `retryModes` — server-driven, never hard-coded by
 *  stage name — offers more than one option, the operator picks via radio and
 *  confirms with a single Proceed button; an older backend that omits
 *  `retryModes` (or a point that only ever offers `RESET`) falls back to a
 *  plain confirm with no picker, today's only behavior. The retry `reason` is
 *  generated silently by `useRetryWorkflow`. */
export function WorkflowRetryDialog({ open, jobId, workflowId, failurePoints, onClose }: Props) {
  const { mutate: retry, isPending } = useRetryWorkflow(jobId, workflowId);
  return (
    <WorkflowRetryDialogView
      open={open}
      failurePoints={failurePoints}
      isPending={isPending}
      onClose={onClose}
      onConfirm={({ runId, eventId, mode }) => {
        retry({ runId, eventId, mode }, { onSuccess: onClose });
      }}
    />
  );
}

/** What the operator confirmed: the run + reset event of the last failure point, and the mode. */
export interface WorkflowRetrySelection {
  runId: string;
  eventId: number;
  mode: WorkflowRetryMode;
  /** The workflow the failure-points response named (may be a child run of the batch). */
  workflowId: string;
}

interface ViewProps {
  open: boolean;
  failurePoints: WorkflowFailurePointsResponse | undefined;
  /** Retry request in flight — locks the dialog and spins the Proceed button. */
  isPending: boolean;
  onConfirm: (selection: WorkflowRetrySelection) => void;
  onClose: () => void;
  /** False hides the RESUME_REMAINING picker and always confirms `RESET` — for retry
   *  endpoints that take no `mode` (e.g. the Failed batch review proxy). Default true. */
  allowModes?: boolean;
}

/** Presentational retry confirm, decoupled from the catalog-operations retry endpoint so other
 *  surfaces (Failed batch review) can reuse the same failure-point UI with their own mutation. */
export function WorkflowRetryDialogView({
  open,
  failurePoints,
  isPending,
  onConfirm,
  onClose,
  allowModes = true,
}: ViewProps) {
  const { t } = useTranslation('catalog-operations');
  // Ordered chronologically (oldest first) — the pipeline halts at the first
  // failure, and any later entry is the actual retry target.
  const failurePoint = failurePoints?.failurePoints.at(-1);
  // History past retention / no reset point: explain instead of an empty confirm.
  const blocker = getRetryBlocker(failurePoints);
  const modes: WorkflowRetryMode[] =
    allowModes && failurePoint?.retryModes?.length ? failurePoint.retryModes : ['RESET'];
  const canResumeRemaining = modes.includes('RESUME_REMAINING');

  // Defaults to the primary recommended action when both are offered. Falls
  // back to `RESET` if a stale selection no longer matches the current point's
  // modes (e.g. failure points refetched after a 400).
  const [selectedMode, setSelectedMode] = useState<WorkflowRetryMode>('RESUME_REMAINING');
  const effectiveMode: WorkflowRetryMode =
    selectedMode === 'RESUME_REMAINING' && !canResumeRemaining ? 'RESET' : selectedMode;

  function handleConfirm() {
    if (!failurePoints || !failurePoint) return;
    onConfirm({
      runId: failurePoints.runId,
      eventId: failurePoint.resetToEventId,
      mode: effectiveMode,
      workflowId: failurePoints.workflowId,
    });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen && !isPending) onClose();
      }}
    >
      <DialogContent
        data-testid="batch-detail-retry-dialog"
        className="max-w-md"
        onInteractOutside={(e) => {
          if (isPending) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isPending) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle
            data-testid="batch-detail-retry-dialog-title"
            className="text-lg font-semibold"
          >
            {t('retry.confirmTitle')}
          </DialogTitle>
        </DialogHeader>

        <p className="text-muted-foreground text-sm">{t('retry.confirmBody')}</p>

        {blocker && (
          <p
            role="alert"
            data-testid="batch-detail-retry-dialog-blocker-banner"
            className="bg-warning-50 border-warning/30 text-foreground rounded-lg border px-3 py-2.5 text-sm"
          >
            {blocker.kind === 'history-unavailable'
              ? (blocker.reason ?? t('retry.historyUnavailable'))
              : t('retry.noFailurePoint')}
          </p>
        )}

        {failurePoint && (
          <div
            data-testid="batch-detail-retry-dialog-failure-point"
            className="bg-error-50 border-error/30 space-y-1 rounded-lg border px-3 py-2.5 text-sm"
          >
            <p className="text-foreground font-medium">{failurePoint.activityType}</p>
            <p className="text-muted-foreground">
              {t('retry.failureLabel')} {formatDateTime(failurePoint.failedAt)}
            </p>
            <p className="text-muted-foreground">{failurePoint.failureMessage}</p>
          </div>
        )}

        {failurePoint && canResumeRemaining && (
          <RadioGroup
            value={effectiveMode}
            onValueChange={(v) => {
              setSelectedMode(v as WorkflowRetryMode);
            }}
            className="gap-2"
            data-testid="batch-detail-retry-dialog-mode-radio-group"
          >
            <label
              htmlFor="retry-mode-resume"
              className="hover:border-primary/50 has-[:checked]:border-primary has-[:checked]:bg-primary/5 flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5"
            >
              <RadioGroupItem
                id="retry-mode-resume"
                data-testid="batch-detail-retry-dialog-mode-resume-remaining-radio"
                value="RESUME_REMAINING"
                className="mt-0.5 size-4"
              />
              <div className="min-w-0 space-y-1">
                <p className="text-foreground text-sm font-medium">
                  {t('retry.modes.resumeRemaining.label')}
                </p>
                <p className="text-muted-foreground text-xs">
                  {t('retry.modes.resumeRemaining.description')}
                </p>
              </div>
            </label>
            <label
              htmlFor="retry-mode-reset"
              className="hover:border-primary/50 has-[:checked]:border-primary has-[:checked]:bg-primary/5 flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5"
            >
              <RadioGroupItem
                id="retry-mode-reset"
                data-testid="batch-detail-retry-dialog-mode-reset-radio"
                value="RESET"
                className="mt-0.5 size-4"
              />
              <div className="min-w-0 space-y-1">
                <p className="text-foreground text-sm font-medium">
                  {t('retry.modes.resetWithAlternative.label')}
                </p>
                <p className="text-muted-foreground text-xs">
                  {t('retry.modes.resetWithAlternative.description')}
                </p>
              </div>
            </label>
          </RadioGroup>
        )}

        <DialogFooter className="gap-2">
          <Button
            data-testid="batch-detail-retry-dialog-cancel-button"
            variant="outline"
            className="cursor-pointer"
            disabled={isPending}
            onClick={onClose}
          >
            {t('retry.cancelButton')}
          </Button>
          <Button
            data-testid="batch-detail-retry-dialog-confirm-button"
            variant="default"
            className="cursor-pointer disabled:cursor-not-allowed"
            isLoading={isPending}
            disabled={isPending || !failurePoint}
            onClick={handleConfirm}
          >
            {t('retry.proceedButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
